import type { MetaDescriptor } from '@remix-run/react';
import { parseURL } from '@archipro-website/top-navigation';
import { noIndexNoFollow } from '@modules/seo/util/no-index-no-follow';
import { hasSortParam } from './has-sort-param';

export const NON_FILTER_KEYS = ['page', 'sort', 'pageSize', 'updateUserPrefs'];

/**
 * Index page for no filter product category, or a single colour/material filter combo that
 * the backend indexability verdict approves (unique SEO content exists AND enough supply —
 * the same predicate gating the sitemap). The verdict defaults to non-indexable when
 * absent, matching the historically conservative default for product filter pages.
 *
 * Non-indexable pages get `noindex` only (never `nofollow`) so link equity keeps flowing
 * through filter pages to products and suppliers.
 * @param metaDescriptors
 * @param url
 * @param comboIndexable backend `IsIndexableFilterPage` verdict for the applied filter
 * @returns
 */
export const noIndexProductCategory = (
    metaDescriptors: MetaDescriptor[],
    url: string,
    comboIndexable: boolean | null | undefined = false,
): MetaDescriptor[] => {
    const { basePath, params } = parseURL(url);
    if (!basePath.startsWith('/products')) return metaDescriptors;

    let filterCount = 0;
    const keys: string[] = [];
    Object.keys(params).forEach((key) => {
        if (NON_FILTER_KEYS.includes(key)) return;
        const values = params[key];
        filterCount += Array.isArray(values) ? values.length : 1;
        keys.push(key);
    });

    const indexableKeys = ['materials', 'colors'];

    const indexPage =
        !hasSortParam(params) &&
        (!filterCount ||
            (comboIndexable === true &&
                filterCount === 1 &&
                keys[0] &&
                indexableKeys.includes(keys[0])));

    return noIndexNoFollow(true, false, !!indexPage, metaDescriptors);
};
